<template>
    <div
        class="basket pin-r absolute block w-full rounded-sm bg-primary"
        data-testid="basket"
    >
        <div
            v-if="!visualMenu"
            :class="[itemCount === 0 ? 'p-0 md:p-4' : 'p-4']"
            class="basket-action select-none relative flex hidden flex-wrap justify-between bg-secondary sm:block"
        >
            <button
                :class="{
                    'basket-action__button-disabled md:block': !canCheckout
                }"
                :disabled="!canCheckout"
                class="basket-action__button border-button-primary flex w-full justify-center clip-path-button rounded-button rounded-sm bg-button font-button p-3 text-center text-sm font-bold text-button md:text-base"
                @click="shouldShowCrossellOrNavigate(false)"
            >
                <span
                    v-if="canCheckout && itemCount > 0"
                    class="item-count inline-block rounded-sm md:hidden"
                    >{{ cartQuantity }}</span
                >
                <span v-if="isClosed" class="rounded-sm">
                    {{ $t('basket.closed') }}
                </span>
                <span v-else-if="nonAlcoholicItemRequired && itemCount > 0">
                    Non alcoholic item required
                </span>
                <span v-else-if="checkoutConditions()">
                    {{ checkFulfillmentMethod() }}
                </span>
                <span v-else>
                    {{ $t('basket.nextCheckout') }}
                </span>

                <span class="total inline-block pr-1 md:hidden">{{
                    (cartTotalWithDiscount / 100) | customCurrency
                }}</span>
            </button>
        </div>
        <!-- Desktop view -->
        <div
            v-if="!showMobileBasket && itemCount > 0 && !visualMenu"
            :class="[itemCount === 0 ? 'p-0 md:p-3' : 'p-3']"
            class="basket-action select-none basket-action__checkout relative flex flex-wrap justify-between bg-secondary sm:hidden"
        >
            <div
                v-if="totalAmountDiscounted"
                class="mb-2 flex flex-auto font-bold flex-row items-center text-sm justify-between px-3 text-subheading"
            >
                <span class="total">
                    {{
                        $t('basket.mobileDiscount', {
                            value: totalAmountDiscountedFormatted
                        })
                    }}
                </span>
            </div>
            <span
                class="basket-action__button border-button-primary flex w-full justify-center clip-path-button rounded-button rounded-sm bg-button font-button p-3 text-center text-sm font-bold text-button md:text-base"
                @click="shouldShowCrossellOrNavigate(true)"
            >
                <span
                    v-if="canCheckout && itemCount > 0"
                    class="item-count inline-block rounded-sm md:hidden"
                >
                    {{ cartQuantity }}
                </span>
                <span v-if="isClosed" class="rounded-sm">
                    {{ $t('basket.closed') }}
                </span>
                <span v-else-if="nonAlcoholicItemRequired">
                    Non alcoholic item required
                </span>
                <span v-else-if="checkoutConditions()">
                    {{ checkFulfillmentMethod() }}
                </span>
                <span v-else>
                    {{ $t('basket.nextViewOrder') }}
                </span>
                <span
                    v-if="monetaryDisplayStrategy !== 'hide_all'"
                    class="total inline-block pr-1 md:hidden"
                >
                    {{ (cartTotalWithDiscount / 100) | customCurrency }}
                </span>
                <span v-else></span>
            </span>
        </div>
        <!-- Mobile view -->
        <div
            v-if="showMobileBasket && cartItems.length > 0 && !visualMenu"
            :class="[itemCount === 0 ? 'p-0 md:p-4' : 'p-4']"
            class="basket-action select-none relative flex flex-wrap justify-between bg-secondary sm:hidden"
        >
            <!-- Shown if user can checkout from the basket (i.e. add to bill, no payments) -->
            <button
                v-if="isPatEnabled && hasCheckoutFromBasket"
                :class="{
                    'spinner white': isLoading,
                    'basket-action__button-disabled':
                        !canCheckout || checkOrderNotes
                }"
                :disabled="isLoading || !canCheckout || checkOrderNotes"
                class="basket-action__button border-button-primary bg-button font-button text-button flex w-full !justify-center clip-path-button rounded-button rounded-sm p-3 text-center text-sm font-bold md:text-base"
                @click="directCheckout()"
            >
                <span class="inline-block">
                    {{ $t('basket.checkoutFromBasketButton') }}
                </span>
            </button>

            <!-- Shown if user can continue to checkout -->

            <router-link
                v-else
                :class="{
                    'basket-action__button-disabled':
                        !canCheckout || checkOrderNotes
                }"
                :disabled="!canCheckout || checkOrderNotes"
                :to="{ name: 'checkout' }"
                class="basket-action__button border-button-primary bg-button font-button text-button flex w-full justify-center clip-path-button rounded-button rounded-sm p-3 text-center text-sm font-bold md:text-base"
            >
                <span
                    v-if="canCheckout && itemCount > 0"
                    class="item-count inline-block rounded-sm md:hidden"
                    >{{ cartQuantity }}</span
                >
                <span v-if="isClosed" class="rounded-sm">
                    {{ $t('basket.closed') }}
                </span>
                <span v-else-if="nonAlcoholicItemRequired">
                    Non alcoholic item required
                </span>
                <span v-else-if="checkoutConditions()">
                    {{ checkFulfillmentMethod() }}
                </span>
                <span v-else-if="checkOrderNotes"
                    >Order notes are required</span
                >
                <!-- @TODO use service stt -->

                <span
                    v-else-if="
                        billId ||
                        venueId === 8376 ||
                        venueId === 9708 ||
                        venueId === 9710 ||
                        venueId === 9714 ||
                        venueId === 9711 ||
                        venueId === 9713 ||
                        venueId === 9712 ||
                        venueId === 10243 ||
                        venueId === 10175 ||
                        venueId === 10176
                    "
                    >{{ $t('basket.nextPlaceOrder') }}</span
                >
                <span v-else>
                    {{ $t('basket.nextCheckout') }}
                </span>
                <span
                    v-if="monetaryDisplayStrategy !== 'hide_all'"
                    class="total inline-block pr-1 md:hidden"
                    >{{ (cartTotalWithDiscount / 100) | customCurrency }}</span
                >
                <span v-else class="w-8"></span>
            </router-link>

            <span
                class="mt-1 flex w-full w-full justify-center rounded-sm bg-secondary p-3 text-center text-sm font-bold text-secondaryactions"
                @click="setShowMobileBasket(false)"
            >
                {{ $t('basket.backToMenu') }}
            </span>
        </div>
        <div
            v-if="!visualMenu"
            :class="{ 'mobile-active': showMobileBasket }"
            class="basket-contents overflow-y-auto relative bg-primary sm:bg-secondary sm:pt-4 md:block"
        >
            <div class="flex flex-col items-start justify-start">
                <div
                    class="basket-contents__header block w-full py-3 px-4 shadow-sm sm:hidden"
                >
                    <h2 class="font-heading text-xl font-bold text-subheading">
                        {{ $t('basket.myBasket') }}
                    </h2>
                    <div class="absolute right-0 top-0 pr-3 pt-3">
                        <button
                            aria-label="Hide basket"
                            class="text-primary transition duration-150 ease-in-out hover:text-gray-500 focus:text-gray-500 focus:outline-none"
                            type="button"
                            @click="setShowMobileBasket(false)"
                        >
                            <close-icon />
                        </button>
                    </div>
                </div>
                <div
                    :class="{ show: showFade }"
                    class="relative no-scrollbar bg-secondary self-stretch overflow-y-auto pt-0 fade-container"
                >
                    <div
                        v-if="hasOverflow"
                        class="absolute z-10 bottom-3 left-1/2 transform -translate-x-1/2"
                    >
                        <base-button
                            button-text="View more"
                            class="flex items-center py-1 px-6"
                            rounded="rounded-2xl"
                            size="sm"
                            @clicked="scrollToBottom"
                        >
                            <template #slot>
                                <arrow-down-icon class="mr-2 h-3 w-3" />
                            </template>
                        </base-button>
                    </div>
                    <div
                        ref="basketItemsContainer"
                        :class="{
                            'no-scrollbar': showMobileBasket,
                            'full-height-basket': isFullHeightMenu
                        }"
                        class="basket-contents__items pt-4 sm:pt-0"
                    >
                        <ul
                            ref="basketList"
                            class="basket-contents__items-list list-none px-5 pb-4 sm:pb-5"
                        >
                            <transition-group
                                v-if="cartItems.length > 0"
                                name="list"
                                tag="div"
                                @after-leave="checkOverflow"
                            >
                                <li
                                    v-for="(item, index) in cartItems"
                                    :key="'item-' + item.id + '-' + index"
                                    :class="{ 'items-center': isPatEnabled }"
                                    class="basket-item flex flex-auto flex-row items-start justify-between text-subheading"
                                >
                                    <cart-image
                                        v-if="
                                            item.image &&
                                            $store.state.venue.venue
                                                .cartImages !== 'disabled'
                                        "
                                        :item="item"
                                        :size="
                                            $store.state.venue.venue.cartImages
                                        "
                                        class="mr-3"
                                    />
                                    <span
                                        class="item mr-3 flex flex-auto flex-col flex-wrap justify-start text-left"
                                    >
                                        <span class="name flex-auto">
                                            {{ item.name }}
                                        </span>

                                        <basket-line-item-modifiers
                                            v-if="item.modifiers?.length > 0"
                                            :modifiers="item.modifiers"
                                        />

                                        <item-line-notes
                                            v-if="item.notes?.length > 0"
                                            :notes="item.notes"
                                        />
                                        <span
                                            v-if="
                                                !isPatEnabled &&
                                                monetaryDisplayStrategy !==
                                                    'hide_all'
                                            "
                                            class="price text-price text-sm"
                                        >
                                            <loading-dots
                                                v-if="isCalculationLoading"
                                            />
                                            <span v-else>
                                                {{
                                                    (calculateItemTotal(item) /
                                                        100)
                                                        | customCurrency
                                                }}
                                            </span>

                                            <span
                                                v-if="
                                                    showDiscountValue(item) &&
                                                    !isCalculationLoading
                                                "
                                                class="text-sm text-green-700"
                                            >
                                                <br />
                                                {{
                                                    `-${
                                                        showDiscountValue(
                                                            item
                                                        ) / 100
                                                    }` | customCurrency
                                                }}
                                            </span>
                                        </span>
                                    </span>

                                    <span>
                                        <div
                                            class="quantity-stepper-small relative inline-flex select-none flex-row items-center justify-between"
                                        >
                                            <div
                                                class="flex cursor-pointer items-center"
                                                @click="
                                                    handleProductRemoval([
                                                        item,
                                                        -1,
                                                        item.modifiers
                                                    ])
                                                "
                                            >
                                                <bin-icon
                                                    v-if="item.quantity === 1"
                                                    class="text-secondaryactions"
                                                />
                                                <minus-circle-icon
                                                    v-else
                                                    class="h-5 w-5 text-secondaryactions"
                                                />
                                            </div>
                                            <div
                                                class="w-6 text-center text-itembody"
                                            >
                                                <animated-quantity
                                                    :value="item.quantity"
                                                />
                                            </div>
                                            <div
                                                class="flex cursor-pointer items-center"
                                                @click="
                                                    validateAndAddProductToCart(
                                                        [
                                                            item,
                                                            1,
                                                            item.modifiers
                                                        ]
                                                    )
                                                "
                                            >
                                                <plus-circle-icon
                                                    :class="{
                                                        'text-gray-400':
                                                            item.trackInventory &&
                                                            item.inventory <=
                                                                item.quantity
                                                    }"
                                                    class="h-5 w-5"
                                                />
                                            </div>
                                        </div>
                                    </span>
                                </li>
                            </transition-group>
                            <li
                                v-if="cartItems.length > 0 && isMobile"
                                class="pt-4 flex justify-end sm:hidden"
                            >
                                <button
                                    class="bg-button rounded-button rounded-sm px-2 py-1 text-right text-xs font-bold text-button cursor-pointer"
                                    @click="setShowMobileBasket(false)"
                                >
                                    {{ $t('basket.addMore') }}
                                </button>
                            </li>
                            <li
                                v-if="!cartItems.length"
                                class="py-8 text-center"
                            >
                                <span class="text-gray-600">
                                    {{ $t('basket.empty') }}
                                </span>

                                <span
                                    v-if="isMobile"
                                    class="mt-6 flex w-full justify-center clip-path-button rounded-button rounded-sm bg-button font-button p-3 text-center text-sm font-bold text-white"
                                    @click="setShowMobileBasket(false)"
                                >
                                    {{ $t('basket.backToMenu') }}
                                </span>
                            </li>
                            <menu-recommendations
                                v-if="
                                    cartItems.length > 0 &&
                                    monetaryDisplayStrategy === 'hide_all'
                                "
                                id="basket-recs"
                                :instance-id="'basket'"
                                class="mt-2 flex w-full border-t border-gray-100"
                                location="basket"
                            />
                        </ul>
                    </div>

                    <table-other-baskets />
                </div>
                <div
                    v-if="
                        cartItems.length > 0 &&
                        monetaryDisplayStrategy !== 'hide_all'
                    "
                    class="basket-contents__footer w-full pt-4 pb-36 sm:pb-4"
                >
                    <menu-recommendations
                        v-if="hasShapedRecs()"
                        id="basket-recs"
                        :instance-id="'basket'"
                        class="mt-2 flex px-5 w-full border-t border-gray-100"
                        location="basket"
                    />
                    <div
                        v-if="isMobile && oderNotesVisibility"
                        class="-mt-4 border-b border-gray-100 bg-secondary px-5"
                    >
                        <order-notes />
                    </div>
                    <ul class="basket-totals list-none">
                        <li
                            v-if="isDelivery"
                            class="mt-2 flex flex-auto flex-row justify-between px-5 text-subheading"
                        >
                            <span class="total">
                                {{ $t('basket.deliveryFee') }}
                            </span>
                            <span
                                :class="{
                                    'flex flex-1 justify-end line-through':
                                        hasDeliveryDiscount
                                }"
                                class="price"
                            >
                                {{ (deliveryFee / 100) | customCurrency }}
                            </span>
                            <span
                                v-if="hasDeliveryDiscount"
                                class="ml-1 flex items-center text-sm"
                            >
                                {{ formattedDeliveryFee }}
                            </span>
                        </li>
                        <li
                            v-if="serviceCharge > 0"
                            class="mt-2 flex flex-auto flex-row justify-between px-5 text-subheading"
                        >
                            <span class="total">{{
                                serviceChargeLabel || $t('basket.serviceCharge')
                            }}</span>
                            <span class="price">{{
                                (serviceCharge / 100) | customCurrency
                            }}</span>
                        </li>
                        <li
                            v-if="defaultServiceChargeEnabled"
                            class="mt-2 flex flex-auto flex-row justify-between px-5"
                        >
                            <div
                                class="mt-1 flex items-center text-base text-gray-600 text-secondary"
                            >
                                <span class="mr-1">
                                    {{
                                        tipLabel || $t('basket.serviceCharge')
                                    }}</span
                                >
                                <span
                                    v-if="!hasMandatoryServiceCharge"
                                    class="text-gray-400"
                                >
                                    <svg
                                        class="ml-1 h-5 w-5 cursor-pointer"
                                        fill="currentColor"
                                        viewBox="0 0 20 20"
                                        xmlns="http://www.w3.org/2000/svg"
                                        @click="showMandatoryTipModal"
                                    >
                                        <path
                                            clip-rule="evenodd"
                                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z"
                                            fill-rule="evenodd"
                                        />
                                    </svg>
                                </span>
                            </div>
                            <span class="price text-price">{{
                                (tip / 100) | customCurrency
                            }}</span>
                        </li>
                        <li
                            v-else-if="tip > 0 && tipVariant === 'enabled'"
                            class="mt-2 flex flex-auto flex-row justify-between px-5 text-subheading"
                        >
                            <span class="total">
                                {{ $t('basket.tip') }}
                            </span>
                            <span class="price">{{
                                (tip / 100) | customCurrency
                            }}</span>
                        </li>

                        <li
                            v-if="totalAmountDiscounted"
                            class="mt-2 flex flex-auto flex-row justify-between px-5 text-subheading"
                        >
                            <span class="total">
                                {{ $t('basket.discount') }}
                            </span>
                            <loading-dots v-if="isCalculationLoading" />
                            <span v-else class="price text-green-700">{{
                                (totalAmountDiscounted / 100) | customCurrency
                            }}</span>
                        </li>
                        <li
                            v-if="
                                discountCodeDetails &&
                                discountCodeDetails.minimumAmount &&
                                discountCodeDetails.minimumAmount - subTotal > 0
                            "
                            class="mt-2 flex flex-auto flex-row justify-between px-5 text-subheading"
                        >
                            <span class="total">
                                {{ $t('checkout.discountCode') }}
                            </span>
                            <span
                                v-if="discountCodeDetails.minimumAmount"
                                class="price text-red-600"
                            >
                                {{ $t('checkout.spend') }}
                                {{
                                    ((discountCodeDetails.minimumAmount -
                                        subTotal) /
                                        100)
                                        | customCurrency
                                }}
                                {{ $t('checkout.more') }}
                            </span>
                        </li>
                        <li
                            class="mt-2 flex flex-auto flex-row justify-between px-5 font-heading font-bold text-heading"
                        >
                            <span class="total">
                                {{ $t('basket.total') }}
                            </span>
                            <loading-dots v-if="isCalculationLoading" />
                            <span v-else class="price">
                                {{
                                    (cartTotalWithDiscount / 100)
                                        | customCurrency
                                }}
                            </span>
                        </li>
                        <li
                            v-if="isCateringMethod"
                            class="flex justify-end items-center px-5"
                        >
                            <span
                                class="price-per-person text-xs cursor-pointer"
                                @click="openCalculationMethodModal()"
                            >
                                {{ (pricePerPerson / 100) | customCurrency }}
                                /
                                {{ $t('basket.person') }}
                            </span>
                        </li>
                    </ul>

                    <piggy-text
                        v-if="showEstimatedCredits"
                        :is-calculate="isCalculating"
                        class="px-5"
                    />
                    <como-rewards-list
                        v-if="
                            ((isAuthenticated &&
                                $store.state.venue.venue.comoEnabled) ||
                                $store.state.venue.venue
                                    .comoNonMemberBenefits) &&
                            !$store.state.venue.venue.comoCollectionOnly
                        "
                        @is-calculating="isCalculationLoading = $event"
                    />
                    <piggy-rewards-list
                        v-if="
                            isAuthenticated &&
                            piggySettings?.enabled &&
                            piggySettings.enabledShowRewards &&
                            piggySettings.enabledShowInlineRewardsOnBasket
                        "
                        listDirection="horizontal"
                    />

                    <button
                        v-if="
                            isAuthenticated &&
                            piggySettings?.enabled &&
                            piggySettings.enabledShowRewards &&
                            !piggySettings.enabledShowInlineRewardsOnBasket
                        "
                        class="px-5 text-sm text-subheading"
                        @click="$modal.show('piggy-rewards-modal')"
                        >{{ $t('piggy.viewRewards') }}</button
                    >
                    <customer-auth
                        v-if="
                            (!isAuthenticated &&
                                piggySettings?.enabled &&
                                piggySettings.enabledShowRewards) ||
                            (!isAuthenticated &&
                                $store.state.venue.venue.comoEnabled)
                        "
                        class="mx-auto mt-2 mb-5"
                    />
                </div>
            </div>
        </div>
        <order-notes-modal v-if="oderNotesVisibility" />
        <confirm-item-removal-modal
            @confirm-item-removal="removeProductFromCart"
        />
    </div>
</template>

<script>
import { mapState, mapActions, mapGetters, mapMutations } from 'vuex';
import { calculateItemTotal } from '@/services/cart';
import { loginWithTemporaryToken } from '@/services/comoService';
import { FulfillmentMethodsEnum, PaymentMethodsEnum } from '@/enums';
import { isMatchingProduct } from '@/util/piggyHelper';
import { filterUserInfo } from '@/utils/customer';
import isMobile from '@/helpers/isMobileDevice';
import { debounce } from '@/helpers/debounce';
import OrderNotesModal from '@/components/modals/OrderNotesModal';
import OrderNotes from '@/components/formElements/OrderNotes';
import PlusCircleIcon from '@/components/icons/PlusCircleIcon';
import MinusCircleIcon from '@/components/icons/MinusCircleIcon';
import CloseIcon from '@/components/icons/CloseIcon';
import PiggyText from '@/components/elements/PiggyText';
import PiggyRewardsList from '@/components/PiggyRewardsList';
import CustomerAuth from '@/components/CustomerAuth';
import ItemLineNotes from '@/components/elements/ItemLineNotes';
import BinIcon from '@/components/icons/BinIcon';
import ConfirmItemRemovalModal from '@/components/modals/ConfirmItemRemovalModal';
import ArrowDownIcon from '@/components/icons/ArrowDownIcon';
import MenuRecommendations from '@/components/Recommendations';
const ComoRewardsList = () => import('@/components/ComoRewardsList');
import LoadingDots from '@/components/elements/LoadingDots';
import CartImage from '@/components/elements/CartImage';
import BasketLineItemModifiers from '@/components/elements/BasketLineItemModifiers';
import AnimatedQuantity from '@/components/elements/AnimatedQuantity';
import TableOtherBaskets from '@/components/TableOtherBaskets';

export default {
    name: 'BasketContainer',
    components: {
        BasketLineItemModifiers,
        ComoRewardsList,
        MenuRecommendations,
        ItemLineNotes,
        OrderNotesModal,
        OrderNotes,
        PlusCircleIcon,
        MinusCircleIcon,
        CloseIcon,
        PiggyText,
        PiggyRewardsList,
        CustomerAuth,
        BinIcon,
        ConfirmItemRemovalModal,
        ArrowDownIcon,
        LoadingDots,
        CartImage,
        AnimatedQuantity,
        TableOtherBaskets
    },
    props: {
        showBasket: {
            type: Boolean,
            required: true,
            default: false
        }
    },
    data() {
        return {
            isLoading: false,
            nonAlcoholicItemRequired: false,
            calculateTimeout: null,
            isCalculating: false,
            hasOverflow: false,
            scrollTimeout: null,
            showFade: false,
            debouncedCheckOverflow: null,
            isCalculationLoading: false
        };
    },
    created() {
        if (this.showBasket) {
            this.setShowMobileBasket(true);
        } else {
            this.setShowMobileBasket(false);
        }

        if (!this.customerAuth && this.$route.query.comoToken) {
            this.loginWithTempToken(this.$route.query.comoToken);
        }

        this.isCalculating = true;
        this.calculatePiggyCredits(this.totalWithDiscount / 100);
    },
    beforeMount() {
        if (
            this.tipVariant !== 'disabled' &&
            this.defaultServiceChargeEnabled
        ) {
            this.initTipValue();
        }
    },
    mounted() {
        if (this.isMobile) {
            const container = this.$refs.basketItemsContainer;
            this.debouncedCheckOverflow = debounce(this.checkOverflow, 200);

            if (container) {
                container.addEventListener('scroll', this.handleBasketScroll);
            }
        }
    },
    beforeDestroy() {
        this.unlockScroll();
        this.removeEventListener();
    },
    watch: {
        // @TODO fix mobile basket scrolling
        // showMobileBasket(value) {
        //     if (value) {
        //         this.lockScroll();
        //         this.checkOverflow();
        //     } else {
        //         this.unlockScroll();
        //     }
        // },
        totalWithDiscount() {
            this.isCalculating = true;

            clearTimeout(this.emailTimeout);

            this.emailTimeout = setTimeout(() => {
                this.calculatePiggyCredits(this.totalWithDiscount / 100);
            }, 100);
        },
        itemCount(newCount, oldCount) {
            if (oldCount > 0 && newCount === 0) {
                this.handleEmptyBasket();
            }
        }
    },
    computed: {
        ...mapState({
            cartItems: state => state.cart.items,
            deliveryFee: state =>
                state.cart.deliveryFee || state.venue.venue.deliveryFee || 0,
            serviceCharge: state => state.venue.venue.serviceCharge || 0,
            serviceChargeLabel: state =>
                state.venue.venue.serviceChargeLabel || '',
            tipLabel: state => state.venue.venue.tipLabel || '',
            fulfillmentMethod: state => state.cart.fulfillmentMethod,
            enabled: state => state.venue.venue.enabled,
            minDeliveryAmount: state => state.venue.venue.minDeliveryAmount,
            minPickupAmount: state => state.venue.venue.minPickupAmount,
            minInStoreAmount: state => state.venue.venue.minInStoreAmount,
            maxDeliveryAmount: state => state.venue.venue.maxDeliveryAmount,
            maxPickupAmount: state => state.venue.venue.maxPickupAmount,
            maxInStoreAmount: state => state.venue.venue.maxInStoreAmount,
            minItemQuantity: state => state.venue.venue.minItemQuantity,
            venueId: state => state.venue.venue.id,
            venueSlug: state => state.venue.venue.slug,
            enableOrderNotes: state => state.venue.venue.enableOrderNotes,
            visualMenu: state => state.venue.venue.visualMenu,
            tableId: state => state.cart.selectedTableId,
            defaultServiceChargeEnabled: state =>
                state.venue.venue.defaultServiceChargeEnabled,
            totalWithDiscount: state => state.cart.totalWithDiscount,
            piggySettings: state => state.venue.venue.piggySettings,
            customerEmail: state => state.customer.email,
            notes: state => state.customer.notes,
            piggyProductDiscount: state => state.cart.piggyProductDiscount,
            customerAuth: state => state.customer.auth,
            zoneDeliveryMinCost: state => state.cart.zoneDeliveryMinCost,
            discountedDeliveryFee: state => state.cart.discountedDeliveryFee,
            monetaryDisplayStrategy: state =>
                state.venue.venue.monetaryDisplayStrategy
        }),
        ...mapGetters({
            cartQuantity: 'cart/cartQuantity',
            subTotal: 'cart/cartSubtotal',
            totalAmountDiscounted: 'cart/totalAmountDiscounted',
            tip: 'cart/tip',
            autoAppliedDiscountCode: 'venue/autoAppliedDiscountCode',
            showMobileBasket: 'basket/showMobileBasket',
            tipVariant: 'venue/tipVariant',
            venueValue: 'venue/venueValue',
            discountCodeDetails: 'discount/details',
            categoryExists: 'menu/categoryExists',
            billId: 'bill/id',
            cartTotalWithDiscount: 'cart/cartTotalWithDiscount',
            pricePerPerson: 'cart/getCalculatedPricePerPerson',
            auth: 'customer/auth',
            isPatEnabled: 'venue/isPatEnabled',
            isClosed: 'venue/isClosed'
        }),
        isMobile() {
            return isMobile();
        },
        itemCount() {
            return this.cartItems.length;
        },
        hasRecommendationsEnabled() {
            return this.$growthbook.isOn('shaped_recs');
        },
        minimumDeliveryAmount() {
            if (this.zoneDeliveryMinCost) {
                return this.zoneDeliveryMinCost;
            } else {
                return this.minDeliveryAmount;
            }
        },
        isFullHeightMenu() {
            return this.monetaryDisplayStrategy === 'hide_all';
        },
        canCheckout() {
            if (!this.enabled || this.itemCount === 0 || this.isClosed) {
                return false;
            }

            // Check minimum item quantity
            if (
                this.minItemQuantity &&
                this.cartQuantity < this.minItemQuantity
            ) {
                return false;
            }

            const limits = {
                [FulfillmentMethodsEnum.DELIVERY]: {
                    min: this.minimumDeliveryAmount,
                    max: this.maxDeliveryAmount
                },
                [FulfillmentMethodsEnum.CATERING_DELIVERY]: {
                    min: this.minimumDeliveryAmount,
                    max: this.maxDeliveryAmount
                },
                [FulfillmentMethodsEnum.PICKUP]: {
                    min: this.minPickupAmount,
                    max: this.maxPickupAmount
                },
                [FulfillmentMethodsEnum.CATERING_PICKUP]: {
                    min: this.minPickupAmount,
                    max: this.maxPickupAmount
                },
                [FulfillmentMethodsEnum.IN_STORE]: {
                    min: this.minInStoreAmount,
                    max: this.maxInStoreAmount
                }
            };

            const methodLimits = limits[this.fulfillmentMethod];
            // Check if subtotal is within allowed range
            return !(
                (methodLimits.min && this.subTotal < methodLimits.min) ||
                (methodLimits.max && this.subTotal > methodLimits.max)
            );
        },
        showEstimatedCredits() {
            return !!(
                this.piggySettings &&
                this.piggySettings?.enabled &&
                this.piggySettings?.loyaltyOptIn?.enabledLoyalty
            );
        },
        accountId() {
            return this.venueValue('accountId');
        },
        isAuthenticated() {
            return this.auth;
        },
        checkOrderNotes() {
            return (
                this.enableOrderNotes === 'mandatory' && this.notes.length === 0
            );
        },
        oderNotesVisibility() {
            return this.enableOrderNotes !== 'hidden';
        },
        hasMandatoryServiceCharge() {
            return this.$growthbook.isOn('mandatory_service_charge');
        },
        totalAmountDiscountedFormatted() {
            return this.$options.filters.customCurrency(
                this.totalAmountDiscounted / -100
            );
        },
        isCateringMethod() {
            if (this.venueId === 9133 || this.venueId === 9469) {
                return true;
            }
            return FulfillmentMethodsEnum.isCateringMethod(
                this.fulfillmentMethod
            );
        },
        formattedDeliveryFee() {
            return this.discountedDeliveryFee === 0
                ? this.$t('basket.free')
                : this.$options.filters.customCurrency(
                      this.discountedDeliveryFee / 100
                  );
        },
        hasDeliveryDiscount() {
            return (
                this.isDelivery &&
                this.discountCodeDetails?.minimumAmount <= this.cartSubtotal &&
                this.discountedDeliveryFee !== this.deliveryFee
            );
        },
        hasCheckoutFromBasket() {
            return this.$growthbook.isOn('checkout_from_basket');
        },
        isInStore() {
            return this.fulfillmentMethod === FulfillmentMethodsEnum.IN_STORE;
        },
        isDelivery() {
            return this.fulfillmentMethod === FulfillmentMethodsEnum.DELIVERY;
        }
    },
    methods: {
        ...mapActions({
            addProductToCart: 'cart/addProductToCart',
            setCartTotalWithDiscount: 'cart/cartTotalWithDiscount',
            setShowMobileBasket: 'basket/setShowMobileBasket',
            initTipValue: 'cart/initTipValue',
            calculateEstimatedCredits: 'customer/calculateEstimatedCredits',
            updatePiggyDiscount: 'cart/updatePiggyDiscount',
            setPaymentType: 'cart/setPaymentType',
            checkout: 'cart/checkout'
        }),
        ...mapMutations({
            setAuth: 'customer/SET_AUTH'
        }),
        async directCheckout() {
            if (!this.isInStore) {
                return;
            }

            if (this.cartItems?.length === 0) {
                return;
            }

            this.isLoading = true;

            try {
                const userFieldsConfig =
                    this.$store.getters['venue/userInformationSettings'];
                const customer = this.$store.state.customer;

                const { missingFields } = filterUserInfo(
                    userFieldsConfig,
                    customer
                );

                if (missingFields.length > 0) {
                    console.warn(
                        '[DirectCheckout] Missing user fields:',
                        missingFields.join(', ')
                    );

                    return this.$router.push({ name: 'checkout' });
                }

                await this.setPaymentType(PaymentMethodsEnum.CASH);

                await this.checkout();
            } catch (e) {
                console.error('[DirectCheckout] Error:', e);
            } finally {
                this.isLoading = false;
            }
        },
        calculateItemTotal(item) {
            return calculateItemTotal(item);
        },
        validateAndAddProductToCart([product, quantityDelta, modifiers]) {
            if (
                product.trackInventory &&
                product.quantity >= product.inventory
            ) {
                return;
            }

            this.addProductToCart([
                product,
                quantityDelta,
                modifiers,
                'basket'
            ]);
            this.updatePiggyDiscount({
                productId: product.id,
                quantity: product.quantity
            });
            this.setCartTotalWithDiscount();
        },
        handleProductRemoval(product) {
            if (product[0].quantity === 1) {
                this.$modal.show('confirm-item-removal-modal', {
                    product: product
                });
            } else {
                this.removeProductFromCart(product);
            }
        },
        removeProductFromCart(product) {
            this.addProductToCart(product);
            this.removePiggyProductDiscount(product);
            this.clearComoRewards();
            this.setCartTotalWithDiscount();
        },
        handleEmptyBasket() {
            if (this.isMobile && this.showMobileBasket) {
                this.setShowMobileBasket(false);
            } else if (this.$route.name === 'checkout') {
                this.$router.push({ name: 'menu' });
            }
        },
        checkoutConditions() {
            return !this.canCheckout && this.itemCount > 0 && this.enabled;
        },
        checkFulfillmentMethod() {
            // Check minimum item quantity first
            if (
                this.minItemQuantity &&
                this.cartQuantity < this.minItemQuantity
            ) {
                return this.$t('basket.minItemQuantity', {
                    count: this.minItemQuantity
                });
            }

            const methodConfig = {
                [FulfillmentMethodsEnum.PICKUP]: {
                    min: this.minPickupAmount,
                    max: this.maxPickupAmount
                },
                [FulfillmentMethodsEnum.DELIVERY]: {
                    min: this.minimumDeliveryAmount,
                    max: this.maxDeliveryAmount
                },
                [FulfillmentMethodsEnum.IN_STORE]: {
                    min: this.minInStoreAmount,
                    max: this.maxInStoreAmount
                }
            };

            const currentMethod = methodConfig[this.fulfillmentMethod];
            if (!currentMethod) return '';

            const formatAmount = amount =>
                this.$options.filters.currency(amount / 100);

            if (this.subTotal < currentMethod.min) {
                return `${this.$t('basket.spendMore')} ${formatAmount(currentMethod.min)}`;
            }

            return `${this.$t('basket.spendLess')} ${formatAmount(currentMethod.max)}`;
        },

        showMandatoryTipModal() {
            this.$modal.show('mandatory-tip-modal');
        },
        goToPayAtTableLandingPage() {
            this.$router.push({
                name: 'LandingPage',
                params: { tableId: this.tableId }
            });
        },
        checkNonAlcoholicItems() {
            this.nonAlcoholicItemRequired = false;

            const alcoholicItem = this.cartItems.findIndex(({ tags }) =>
                tags.includes(1)
            );

            const nonAlcoholicItem = this.cartItems.findIndex(({ tags }) =>
                tags.includes(3)
            );

            if (~alcoholicItem) {
                if (~nonAlcoholicItem) {
                    return true;
                } else {
                    this.nonAlcoholicItemRequired = true;
                    return false;
                }
            }

            return true;
        },
        hasCrosssellEnabled() {
            const upsellProducts = this.venueValue('upsellProducts');
            if (
                upsellProducts &&
                Array.isArray(upsellProducts) &&
                upsellProducts.length > 0
            ) {
                return true;
            }

            if (
                this.venueValue('crossSellCategory') &&
                this.categoryExists(this.venueValue('crossSellCategory'))
            ) {
                return true;
            }
            return false;
        },
        shouldShowCrossellOrNavigate(isMobile) {
            if (this.hasCrosssellEnabled()) {
                return this.$modal.show('cross-sell-modal', { isMobile });
            }

            isMobile
                ? this.setShowMobileBasket(true)
                : this.$router.push({ name: 'checkout' });
        },
        hasShapedRecs() {
            return true;
        },
        async calculatePiggyCredits(unitValue) {
            if (this.itemCount === 0 || !this.piggySettings?.enabled) {
                return;
            }

            const params = {
                email: this.customerEmail,
                piggyShopId: this.piggySettings.piggyShopId,
                unitValue,
                accountId: this.accountId,
                unitName: this.piggySettings.creditUnitName || '',
                venueId: this.venueId
            };

            await this.calculateEstimatedCredits(params);

            this.isCalculating = false;
        },
        showDiscountValue(item) {
            if (item.discountAmount > 0) {
                return item.discountAmount;
            }

            const modifierIds = item.modifiers.map(modifier => modifier.id);
            const discountValue = this.piggyProductDiscount.find(product =>
                isMatchingProduct(product, item.id, modifierIds)
            );

            return discountValue?.value;
        },
        removePiggyProductDiscount(product) {
            if (product[0].quantity === 0) {
                const modifierIds = product[0].modifiers.map(
                    modifier => modifier.id
                );

                this.$store.commit('cart/REMOVE_PIGGY_PRODUCT_DISCOUNT', {
                    id: product[0].id,
                    modifierIds
                });

                if (!this.piggyProductDiscount.length) {
                    this.$store.commit('cart/SET_PIGGY_REWARD_SELECTED', {});
                }
            }

            this.updatePiggyDiscount({
                productId: product[0].id,
                quantity: product[0].quantity
            });
        },
        lockScroll() {
            this.offsetY = window.pageYOffset;
            document.body.style.top = `${-this.offsetY}px`;
            document.body.classList.add('lock-scroll-position');
        },
        unlockScroll() {
            const offsetY = Math.abs(
                parseInt(document.body.style.top || 0, 10)
            );
            document.body.classList.remove('lock-scroll-position');
            document.body.style.removeProperty('top');
            window.scrollTo(0, offsetY || 0);
        },
        openCalculationMethodModal() {
            this.$modal.show('calculation-method-modal');
        },
        scrollToBottom() {
            const list = this.$refs.basketList;

            if (list) {
                list.scrollIntoView({ behavior: 'smooth', block: 'end' });
            }
        },
        handleBasketScroll() {
            this.hasOverflow = false;
            this.showFade = true;

            if (this.scrollTimeout) {
                cancelAnimationFrame(this.scrollTimeout);
            }
            this.scrollTimeout = requestAnimationFrame(
                this.debouncedCheckOverflow
            );
        },
        checkOverflow() {
            this.scrollTimeout = null;
            const basketItemsContainer = this.$refs.basketItemsContainer;

            if (!basketItemsContainer) {
                return;
            }

            const { scrollTop, scrollHeight, clientHeight } =
                basketItemsContainer;

            if (scrollHeight && clientHeight) {
                this.hasOverflow = scrollTop + clientHeight <= scrollHeight - 1;
                this.showFade = this.hasOverflow;
            }
        },
        removeEventListener() {
            const container = this.$refs.basketItemsContainer;

            if (container) {
                container.removeEventListener(
                    'scroll',
                    this.handleBasketScroll
                );
            }
        },
        clearComoRewards() {
            if (!this.cartItems.length) {
                this.$store.commit('customer/CLEAR_COMO_REWARDS');
            }
        },
        async loginWithTempToken(token) {
            const data = await loginWithTemporaryToken({
                venueId: this.venueId,
                accountId: this.accountId,
                temporaryToken: token
            });

            if (data?.status === 'ok') {
                await this.$store.dispatch('customer/setAuth', {
                    accountId: this.accountId,
                    value: true
                });

                this.fillCustomerDetails(data);
            }
        },
        fillCustomerDetails(data) {
            if (!data.membership) {
                return;
            }

            const { firstName, lastName, phoneNumber, email } = data.membership;

            if (firstName) {
                this.$store.commit('customer/setFirstName', firstName);
            }

            if (lastName) {
                this.$store.commit('customer/setLastName', lastName);
            }

            if (phoneNumber) {
                this.$store.commit('customer/setPhone', phoneNumber);
            }

            if (email) {
                this.$store.commit('customer/setEmail', email);
            }
        }
    }
};
</script>

<style scoped src="@/assets/css/basket_scoped.css"></style>
